import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "../data/content";
import { PageHero, Reveal, useSeo } from "../components/Reveal";
import { QuoteEstimator } from "../components/QuoteEstimator";
import { UrgentButton } from "../components/UrgentModal";

export default function Services() {
  useSeo(
    "Testing Services | Drug, DNA, DOT & Workplace Testing — SwiftTest Solutions",
    "Mobile drug testing, pre-employment screening, workers' comp, DNA, DOT, random, post-accident and reasonable-suspicion testing across Eastern Iowa."
  );

  return (
    <div data-testid="services-page">
      <PageHero
        testId="services-hero"
        eyebrow="Our services"
        title="Comprehensive mobile testing solutions"
        lead="Eight certified services delivered wherever you need them — your office, job site, facility, or home. Straightforward process, confidential handling, fast results."
      />
      <section className="bg-background">
        <div className="mx-auto max-w-5xl space-y-6 px-5 pb-24 pt-16 sm:px-8">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={0.05}>
              <article
                id={s.id}
                data-testid={`service-detail-${s.id}`}
                className="scroll-mt-28 rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition-shadow hover:shadow-lg sm:p-10"
              >
                <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-teal-700 text-white">
                    <s.icon className="h-7 w-7" />
                  </span>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-xs font-bold uppercase tracking-[0.18em] text-teal-700">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h2 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">
                        {s.title}
                      </h2>
                    </div>
                    <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-600">
                      {s.desc}
                    </p>
                    <Link
                      data-testid={`service-request-link-${s.id}`}
                      to={`/contact?service=${encodeURIComponent(s.title)}`}
                      className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-teal-800"
                    >
                      Request this service <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="bg-white">
        <div className="mx-auto max-w-5xl px-5 py-20 sm:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-teal-700">
              <span className="h-px w-8 bg-teal-600/60" />
              Instant ballpark
              <span className="h-px w-8 bg-teal-600/60" />
            </span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Estimate your visit in seconds
            </h2>
          </Reveal>
          <Reveal delay={0.12} className="mt-12">
            <QuoteEstimator />
          </Reveal>
        </div>
      </section>
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-5 py-16 text-center sm:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Facing a deadline or an incident right now?
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-base text-slate-600">
              Post-accident and reasonable-suspicion testing can't wait. We dispatch around the
              clock.
            </p>
            <div className="mt-7 flex justify-center">
              <UrgentButton testId="services-need-testing-now-button" className="px-6 py-3.5 text-base" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
